import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  IconButton, Chip, FormControl, InputLabel, Select, 
  MenuItem, CircularProgress, Alert, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Tooltip, useTheme, useMediaQuery, Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuthStore } from '../../store/authStore';

const BlogComments = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  
  // Status update state
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // Check if user is admin
  useEffect(() => {
    if (user && !['admin', 'superadmin'].includes(user.role)) {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch comments
  const fetchComments = async () => {
    try {
      setLoading(true);
      
      // Используем новый API-эндпоинт для получения всех комментариев
      const response = await axios.get('/api/blog/blog-comments', {
        params: {
          page,
          limit: 10,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          orderBy: 'createdAt',
          order: 'DESC'
        }
      });
      
      // Преобразуем данные для отображения в таблице
      const commentsWithPostInfo = response.data.comments.map(comment => ({
        ...comment,
        postTitle: comment.BlogPost?.title || 'Неизвестная статья',
        postId: comment.BlogPost?.id,
        postSlug: comment.BlogPost?.slug
      }));
      
      setComments(commentsWithPostInfo);
      setTotalPages(response.data.totalPages || 1);
      setLoading(false);
    } catch (err) {
      // Обрабатываем ошибку без вывода в консоль
      setError('Не удалось загрузить комментарии');
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchComments();
  }, [page, statusFilter]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  // Open delete dialog
  const openDeleteDialog = (comment) => {
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
    setDeleteError(null);
  };

  // Delete comment
  const deleteComment = async () => {
    if (!commentToDelete) return;
    
    try {
      setDeleteLoading(true);
      await axios.delete(`/api/blog/blog-comments/${commentToDelete.id}`);
      setDeleteLoading(false);
      closeDeleteDialog();
      fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
      setDeleteError('Не удалось удалить комментарий');
      setDeleteLoading(false);
    }
  };

  // Update comment status
  const updateCommentStatus = async (commentId, status) => {
    try {
      setStatusUpdateLoading(true);
      await axios.put(`/api/blog/blog-comments/${commentId}`, { status });
      setStatusUpdateLoading(false);
      fetchComments();
    } catch (err) {
      console.error('Error updating comment status:', err);
      setStatusUpdateLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'd MMM yyyy HH:mm', { locale: ru });
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Одобрен';
      case 'pending':
        return 'На модерации';
      case 'rejected':
        return 'Отклонен';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Управление комментариями
        </Typography>
      </Box>
      
      {/* Фильтры */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <FormControl variant="outlined" size="small" sx={{ width: { xs: '100%', sm: 200 } }}>
          <InputLabel id="status-filter-label">Статус</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Статус"
            fullWidth
          >
            <MenuItem value="all">Все комментарии</MenuItem>
            <MenuItem value="pending">На модерации</MenuItem>
            <MenuItem value="approved">Одобренные</MenuItem>
            <MenuItem value="rejected">Отклоненные</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Таблица комментариев */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: { xs: 2, sm: 4 } }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>{error}</Alert>
      ) : comments.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <Typography variant="body1">
            Комментарии не найдены
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Автор</TableCell>
                  <TableCell>Комментарий</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Статья</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Дата</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map((comment) => (
                  <TableRow key={comment.id}>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                        {comment.user?.name || 'Пользователь'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ 
                        maxWidth: { xs: 120, sm: 200, md: 300 }, 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                      }}>
                        {comment.content}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" sx={{ 
                        maxWidth: { sm: 150, md: 200 }, 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                      }}>
                        {comment.postTitle}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 1, sm: 2 } }}>
                      {formatDate(comment.createdAt)}
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Chip 
                        label={getStatusLabel(comment.status)} 
                        color={getStatusColor(comment.status)}
                        size="small"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: { xs: 1, sm: 2 } }}>
                      {isMobile ? (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => navigate(`/blog/${comment.postSlug}`)}
                            sx={{ padding: '4px' }}
                          >
                            <VisibilityIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          
                          {comment.status !== 'approved' && (
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => updateCommentStatus(comment.id, 'approved')}
                              disabled={statusUpdateLoading}
                              sx={{ padding: '4px' }}
                            >
                              <CheckCircleIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          )}
                          
                          {comment.status !== 'rejected' && (
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => updateCommentStatus(comment.id, 'rejected')}
                              disabled={statusUpdateLoading}
                              sx={{ padding: '4px' }}
                            >
                              <CancelIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          )}
                          
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => openDeleteDialog(comment)}
                            sx={{ padding: '4px' }}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <Tooltip title="Просмотреть статью">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => navigate(`/blog/${comment.postSlug}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {comment.status !== 'approved' && (
                            <Tooltip title="Одобрить">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => updateCommentStatus(comment.id, 'approved')}
                                disabled={statusUpdateLoading}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {comment.status !== 'rejected' && (
                            <Tooltip title="Отклонить">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => updateCommentStatus(comment.id, 'rejected')}
                                disabled={statusUpdateLoading}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Удалить">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => openDeleteDialog(comment)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Пагинация */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary"
                size={isMobile ? "small" : "medium"}
                siblingCount={isMobile ? 0 : 1}
              />
            </Box>
          )}
        </>
      )}
      
      {/* Диалог подтверждения удаления */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={closeDeleteDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main', 
          color: 'white',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          p: { xs: 2, sm: 3 }
        }}>
          Подтверждение удаления
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
          )}
          <Typography sx={{ mt: 1 }}>
            Вы действительно хотите удалить этот комментарий?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 'medium' }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <Button 
            onClick={closeDeleteDialog} 
            disabled={deleteLoading}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            variant={isMobile ? "outlined" : "text"}
          >
            Отмена
          </Button>
          <Button 
            onClick={deleteComment} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
            sx={{ ml: isMobile ? 0 : 1 }}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlogComments;
